import React, { useState, useRef, useEffect } from 'react';
import { searchUsers } from '../api/rolesApi';
import { registerParticipant, resolveParticipants, registerBatch } from '../api/eventsApi';
import { updateMemberAlias } from '../api/membersApi';
import { getMemberDisplay, MemberAvatar, MemberIdentity } from './memberDisplay';
import { useToast } from './Toast';

const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const IconClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);


const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Debounce hook
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// Статусы строк предпросмотра → бейдж (label + цвета)
const RESOLVE_BADGE = {
  NEW_GUEST:          { label: 'новый гость',    color: '#065f46', bg: '#d1fae5' },
  EXISTING_MEMBER:    { label: 'есть в базе',          color: '#3730a3', bg: '#e0e7ff' },
  REAL_USER_MATCHED:  { label: 'пользователь',         color: '#1e40af', bg: '#dbeafe' },
  PENDING_USERNAME:   { label: 'новый гость · @TG',    color: '#065f46', bg: '#d1fae5' },
  ALREADY_IN_EVENT:   { label: 'уже в событии',  color: '#6b7280', bg: '#f3f4f6' },
  DUPLICATE_IN_INPUT: { label: 'дубль в списке', color: '#6b7280', bg: '#f3f4f6' },
  INVALID:            { label: 'пусто',          color: '#991b1b', bg: '#fee2e2' },
};
// Статусы, которые реально приведут к добавлению
const ADDABLE = new Set(['NEW_GUEST', 'EXISTING_MEMBER', 'REAL_USER_MATCHED', 'PENDING_USERNAME']);
// Статусы, где строка резолвится в уже существующего члена — показываем его текущее имя в базе,
// чтобы владелец заметил расхождение с тем, что ввёл сейчас (без авто-перезаписи).
const SHOWS_EXISTING = new Set(['EXISTING_MEMBER', 'REAL_USER_MATCHED', 'ALREADY_IN_EVENT']);
// Строки, резолвящиеся в уже существующего члена — для них доступна opt-in правка
// имени (гость) / пометки (реальный) прямо в превью (тумблер, по умолчанию выключен).
const EDITABLE_EXISTING = new Set(['EXISTING_MEMBER', 'REAL_USER_MATCHED']);

// Имя/пометка из строки = ввод без @username (как и на бэкенде в classify).
const stripUsername = (line) =>
  (line || '').replace(/@\w+/g, ' ').replace(/\s+/g, ' ').trim();

// Маленький переключатель для opt-in применения правки в превью.
const MiniToggle = ({ on, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    style={{
      position: 'relative', width: 34, height: 19, borderRadius: 10,
      background: on ? '#6366f1' : '#d1d5db', border: 'none', padding: 0,
      flexShrink: 0, cursor: 'pointer', transition: 'background 0.2s',
    }}
  >
    <span style={{
      position: 'absolute', top: 2, left: on ? 17 : 2,
      width: 15, height: 15, borderRadius: '50%', background: '#fff',
      boxShadow: '0 1px 2px rgba(0,0,0,0.25)', transition: 'left 0.2s',
    }} />
  </button>
);

/**
 * Парсит строки списка, извлекая @username из любой позиции.
 * Например, "Никита @knv" → отправляем "@knv" на бэкенд, "Никита" запоминаем локально.
 * Возвращает:
 *   entries — строки для отправки на бэкенд
 *   displayNameOverrides — map username.toLowerCase() → отображаемое имя
 */
const parseListEntries = (text) => {
  const lines = text.split('\n').map(s => s.trim()).filter(Boolean);
  // Шлём строки на бэкенд как есть — бэкенд сам извлекает @username и использует остаток как алиас.
  // rawDisplayLines совпадает с entries, но хранится отдельно для отображения превью.
  return {
    entries: lines,
    displayNameOverrides: {},
    rawDisplayLines: lines,
  };
};

/**
 * Рендерит строку текста, оборачивая @username в кликабельную ссылку.
 * Например: "Ник2 @knv1" → "Ник2 " + <a>@knv1</a>
 */
const renderLineWithUsername = (line) => {
  const match = line.match(/(@\w+)/);
  if (!match) return <span>{line}</span>;
  const idx = line.indexOf(match[1]);
  const before = line.slice(0, idx);
  const after  = line.slice(idx + match[1].length);
  const username = match[1].slice(1); // без @
  return (
    <>
      {before}
      <a
        href={`https://t.me/${username}`}
        target="_blank"
        rel="noreferrer"
        onClick={e => e.stopPropagation()}
        style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}
      >
        {match[1]}
      </a>
      {after}
    </>
  );
};

const AddParticipantPanel = ({ eventId, organizationId, onAdded, defaultMode = 'single' }) => {
  const { addToast } = useToast();
  const [mode, setMode] = useState(defaultMode); // 'single' | 'list'

  // --- single mode state ---
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualName, setManualName] = useState('');
  const [searched, setSearched] = useState(false);
  // Инлайн-правка alias прямо в результатах поиска
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const inputRef = useRef(null);
  // Всплывающее окно решения при одиночном добавлении: ввод отличается от того, что в базе.
  // pendingAdd = { user, namePart, isGuest, currentRef, currentName } | null
  const [pendingAdd, setPendingAdd] = useState(null);
  const [pendingApply, setPendingApply] = useState(false); // тумблер «применить изменение»
  const [pendingValue, setPendingValue] = useState('');
  // Пагинация результатов поиска: подгрузка следующих страниц по кнопке.
  const [searchPage, setSearchPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // --- list mode state ---
  const [listText, setListText] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(null); // null = ещё не проверяли
  const [batchAdding, setBatchAdding] = useState(false);
  // Оригинальный ввод пользователя по индексу — для отображения в превью
  const [rawDisplayLines, setRawDisplayLines] = useState([]);
  // Opt-in правки имени/пометки существующих членов в превью: index → { on, value }.
  // По умолчанию пусто (тумблер выключен) — добавление не переименовывает молча.
  const [applyEdits, setApplyEdits] = useState({});

  const debouncedQuery = useDebounce(query, 400);

  // Размер страницы результатов поиска.
  const SEARCH_PAGE_SIZE = 20;
  // Если во вводе есть @username — ищем по нему (находим существующего члена даже когда
  // рядом введено другое имя, напр. «Никита @knv»). Иначе ищем по всему вводу.
  const buildSearchTerm = (q) => {
    const unameMatch = q.match(/@(\w+)/);
    return unameMatch ? unameMatch[1] : q;
  };

  useEffect(() => {
    if (mode !== 'single' || !debouncedQuery.trim()) {
      setResults([]);
      setSearched(false);
      setHasMore(false);
      setSearchPage(0);
      return;
    }
    setSearching(true);
    setSearched(false);
    // includeGuests = true: при добавлении участников гости должны находиться
    searchUsers(buildSearchTerm(debouncedQuery), organizationId, 0, SEARCH_PAGE_SIZE, null, null, true)
      .then(data => {
        setResults(data?.content || data || []);
        // last === false означает, что есть ещё страницы (для массива-фоллбэка — нет).
        setHasMore(data?.last === false);
        setSearchPage(0);
        setSearched(true);
      })
      .catch(() => {
        setResults([]);
        setHasMore(false);
        setSearched(true);
      })
      .finally(() => setSearching(false));
  }, [debouncedQuery, organizationId, mode]);

  // Подгрузка следующей страницы результатов — дописываем к уже показанным.
  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = searchPage + 1;
    setLoadingMore(true);
    searchUsers(buildSearchTerm(debouncedQuery), organizationId, nextPage, SEARCH_PAGE_SIZE, null, null, true)
      .then(data => {
        setResults(prev => [...prev, ...(data?.content || [])]);
        setHasMore(data?.last === false);
        setSearchPage(nextPage);
      })
      .catch(() => {
        addToast('Не удалось загрузить ещё', 'error');
      })
      .finally(() => setLoadingMore(false));
  };

  // aliasToApply (опц.) — если задан, после добавления применяем правку имени/алиаса члена.
  const handleAddById = async (user, aliasToApply = null) => {
    setAdding(true);
    try {
      // Существующего члена (в т.ч. гостя) добавляем по organizationMemberId — без дублей.
      const payload = user.organizationMemberId != null
        ? { organizationMemberId: user.organizationMemberId }
        : { userId: user.id };
      const reg = await registerParticipant(eventId, payload);
      if (aliasToApply != null && aliasToApply.trim() && user.organizationMemberId != null) {
        await updateMemberAlias(user.organizationMemberId, aliasToApply.trim(), eventId);
      }
      addToast(`${user.displayName || user.firstName || user.telegramUserName} добавлен`, 'success');
      setQuery('');
      setResults([]);
      setSearched(false);
      setPendingAdd(null);
      onAdded?.(reg);
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setAdding(false);
    }
  };

  // Попап решения о переименовании — только на ручном вводе «@ник + имя» (см. handleAddManual).
  // В поиске выбор из результатов добавляет участника как есть; переименовать найденного
  // можно карандашом прямо в строке результата.
  const confirmPendingAdd = () => {
    if (!pendingAdd) return;
    const aliasValue = pendingApply ? pendingValue : null;
    doManualRegister(pendingAdd.manualName, pendingAdd.organizationMemberId, aliasValue);
  };


  const cancelEditResult = () => {
    setEditingMemberId(null);
    setEditDraft('');
  };

  const saveEditResult = async (user) => {
    if (savingEdit) return;
    setSavingEdit(true);
    try {
      const updated = await updateMemberAlias(user.organizationMemberId, editDraft.trim(), eventId);
      // Локально обновляем строку результата, без повторного запроса.
      setResults(rs => rs.map(r => r.organizationMemberId === user.organizationMemberId
        ? { ...r, alias: updated.alias, displayName: updated.displayName }
        : r));
      addToast('Имя обновлено', 'success');
      setEditingMemberId(null);
      setEditDraft('');
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  // Финальная регистрация по ручному вводу (+ опц. правка имени/алиаса существующего члена).
  const doManualRegister = async (name, applyMemberId, aliasValue) => {
    setAdding(true);
    try {
      const reg = await registerParticipant(eventId, { manualName: name });
      const memberId = applyMemberId ?? reg.organizationMemberId;
      if (aliasValue && aliasValue.trim() && memberId != null) {
        await updateMemberAlias(memberId, aliasValue.trim(), eventId);
      }
      addToast(`${name} добавлен`, 'success');
      setManualName('');
      setShowManual(false);
      setQuery('');
      setResults([]);
      setSearched(false);
      setPendingAdd(null);
      onAdded?.(reg);
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleAddManual = async () => {
    if (!manualName.trim()) return;
    const name = manualName.trim();
    setAdding(true);
    // Dry-run: матчится ли ввод в существующего члена с другим именем/алиасом?
    let item = null;
    try {
      const data = await resolveParticipants(eventId, [name]);
      item = data?.items?.[0] || null;
    } catch {
      // резолв некритичен — при ошибке просто добавим как обычно
    }

    const namePart = stripUsername(name);
    const currentAlias = item?.currentAlias || '';
    const needDecision = !!item
      && EDITABLE_EXISTING.has(item.status)
      && item.organizationMemberId != null
      && !!namePart
      && namePart !== currentAlias;

    if (needDecision) {
      setPendingAdd({
        mode: 'manual',
        manualName: name,
        organizationMemberId: item.organizationMemberId,
        namePart,
        isGuest: !!item.guest,
        currentName: item.existingAlias || item.displayName || namePart,
        currentRef: item.guest ? (currentAlias || '—') : (currentAlias || 'не задан'),
      });
      setPendingApply(false);
      setPendingValue(namePart);
      setAdding(false);
      return;
    }

    await doManualRegister(name, null, null);
  };

  // --- list mode handlers ---
  const handleResolve = async () => {
    const { entries, rawDisplayLines: rawLines } = parseListEntries(listText);
    if (entries.length === 0) return;
    setResolving(true);
    try {
      const data = await resolveParticipants(eventId, entries);
      setRawDisplayLines(rawLines);
      setApplyEdits({}); // сбрасываем тумблеры правок при новой проверке
      setResolved(data?.items || []);
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setResolving(false);
    }
  };

  const handleBatchAdd = async () => {
    const { entries } = parseListEntries(listText);
    if (entries.length === 0) return;
    setBatchAdding(true);
    try {
      const res = await registerBatch(eventId, entries);

      // Применяем opt-in правки имени/пометки существующих членов (тумблеры в превью).
      // Делаем после batch-добавления, чтобы член точно существовал и был зарегистрирован.
      const editTasks = Object.entries(applyEdits)
        .filter(([, e]) => e && e.on && (e.value || '').trim())
        .map(([idx, e]) => {
          const item = (resolved || [])[Number(idx)];
          if (!item || item.organizationMemberId == null) return null;
          return updateMemberAlias(item.organizationMemberId, e.value.trim(), eventId);
        })
        .filter(Boolean);
      if (editTasks.length) await Promise.all(editTasks);

      const parts = [`Добавлено ${res.added}`];
      if (res.alreadyRegistered) parts.push(`уже были ${res.alreadyRegistered}`);
      if (res.skipped) parts.push(`пропущено ${res.skipped}`);
      if (editTasks.length) parts.push(`правок имени ${editTasks.length}`);
      addToast(parts.join(', '), 'success');
      setListText('');
      setResolved(null);
      setRawDisplayLines([]);
      setApplyEdits({});
      onAdded?.();
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setBatchAdding(false);
    }
  };

  const noResults = searched && results.length === 0 && query.trim();
  const showManualButton = searched && !showManual;

  const addableCount = resolved ? resolved.filter(r => ADDABLE.has(r.status)).length : 0;
  const entriesCount = parseListEntries(listText).entries.length;

  const tabStyle = (active) => ({
    flex: 1, padding: '6px 0', fontSize: 12, fontWeight: 700, cursor: 'pointer',
    border: 'none', borderRadius: 8,
    background: active ? '#fff' : 'transparent',
    color: active ? '#4f46e5' : '#9ca3af',
    boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
    transition: 'all 0.15s',
  });

  return (
    <div style={{
      background: '#f8fafc',
      borderRadius: 12,
      border: '1.5px dashed #c7d2fe',
      padding: '14px 14px 12px',
      marginTop: 12,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6366f1', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Добавить участника
      </div>

      {/* Всплывающее окно решения: введённое имя отличается от того, что в базе. */}
      {pendingAdd && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: '20px 18px',
            maxWidth: 340, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: '#111827' }}>
              Добавить участника
            </h3>
            <p style={{ margin: '0 0 4px', fontSize: 13, color: '#374151' }}>
              <strong>{pendingAdd.currentName}</strong>
            </p>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
              Вы ввели «{pendingAdd.namePart}», в базе {pendingAdd.isGuest ? 'имя' : 'алиас'}: «{pendingAdd.currentRef}».
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: pendingApply ? 8 : 14 }}>
              <MiniToggle on={pendingApply} onChange={() => setPendingApply(v => !v)} />
              <span style={{ fontSize: 12, color: '#374151' }}>
                {pendingAdd.isGuest ? 'Применить изменение имени' : 'Применить изменение алиаса'}
              </span>
            </div>
            {pendingApply && (
              <input
                value={pendingValue}
                onChange={e => setPendingValue(e.target.value)}
                placeholder={pendingAdd.isGuest ? 'Имя участника...' : 'Алиас...'}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
                  border: '1.5px solid #6366f1', fontSize: 13, color: '#111827', outline: 'none', marginBottom: 14,
                }}
              />
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setPendingAdd(null)}
                disabled={adding}
                style={{
                  flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#f9fafb', fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={confirmPendingAdd}
                disabled={adding}
                style={{
                  flex: 1, padding: '10px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                  fontSize: 14, fontWeight: 600, color: '#fff', cursor: adding ? 'wait' : 'pointer',
                }}
              >
                {adding ? 'Добавляю...' : 'Добавить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mode tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#eef2ff', borderRadius: 10, padding: 3, marginBottom: 12 }}>
        <button style={tabStyle(mode === 'single')} onClick={() => setMode('single')}>По одному</button>
        <button style={tabStyle(mode === 'list')} onClick={() => setMode('list')}>Списком</button>
      </div>

      {mode === 'single' && (
        <>
          {/* Search input */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <div style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: searching ? '#6366f1' : '#9ca3af', pointerEvents: 'none',
            }}>
              <IconSearch />
            </div>
            <input
              ref={inputRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setShowManual(false); }}
              placeholder="Поиск по имени или @username..."
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '9px 32px 9px 34px',
                borderRadius: 9, border: '1.5px solid #e5e7eb',
                fontSize: 14, color: '#111827', background: '#fff',
                outline: 'none',
              }}
              onFocus={e => e.target.style.borderColor = '#6366f1'}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults([]); setSearched(false); setShowManual(false); }}
                style={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9ca3af', padding: 2, display: 'flex',
                }}
              >
                <IconClose />
              </button>
            )}
          </div>

          {/* Search results */}
          {results.length > 0 && (
            <div style={{
              background: '#fff', borderRadius: 9, border: '1.5px solid #e5e7eb',
              overflow: 'hidden', marginBottom: 8,
            }}>
              {results.map((user, i) => {
                // Единый слой отображения (гость vs реальный) — см. memberDisplay.jsx.
                const view = getMemberDisplay(user);
                const isEditing = editingMemberId === user.organizationMemberId;
                return (
                  <div
                    key={user.organizationMemberId ?? user.id ?? i}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px',
                      borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                      transition: 'background 0.1s',
                    }}
                  >
                    {isEditing ? (
                      <>
                        <MemberAvatar view={view} size={30} />
                        <input
                          autoFocus
                          value={editDraft}
                          onChange={e => setEditDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') saveEditResult(user); if (e.key === 'Escape') cancelEditResult(); }}
                          placeholder={view.isGuest ? 'Имя участника...' : 'Пометка...'}
                          style={{
                            flex: 1, minWidth: 0, boxSizing: 'border-box',
                            padding: '6px 8px', borderRadius: 8, border: '1.5px solid #6366f1',
                            fontSize: 13, color: '#111827', outline: 'none',
                          }}
                        />
                        <button onClick={() => saveEditResult(user)} disabled={savingEdit} title="Сохранить"
                          style={{ background: '#dcfce7', border: '1.5px solid #86efac', borderRadius: 8, padding: '5px 6px', color: '#16a34a', cursor: savingEdit ? 'wait' : 'pointer', display: 'flex', flexShrink: 0 }}>
                          <IconCheck />
                        </button>
                        <button onClick={cancelEditResult} disabled={savingEdit} title="Отмена"
                          style={{ background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '5px 6px', color: '#6b7280', cursor: 'pointer', display: 'flex', flexShrink: 0 }}>
                          <IconClose />
                        </button>
                      </>
                    ) : (
                      <>
                        <div
                          style={{ flex: 1, minWidth: 0, cursor: adding ? 'wait' : 'pointer' }}
                          onClick={() => handleAddById(user)}
                        >
                          <MemberIdentity view={view} size={30} />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}

              {/* Кнопка подгрузки следующей страницы результатов */}
              {hasMore && (
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={{
                    width: '100%', padding: '9px 12px',
                    borderTop: '1px solid #f3f4f6', border: 'none',
                    background: '#fff', color: '#6366f1',
                    fontSize: 12, fontWeight: 600,
                    cursor: loadingMore ? 'wait' : 'pointer',
                  }}
                >
                  {loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
                </button>
              )}
            </div>
          )}

          {/* No results hint */}
          {noResults && (
            <div style={{
              fontSize: 13, color: '#6b7280', marginBottom: 8,
              padding: '8px 12px', background: '#fff', borderRadius: 9,
              border: '1.5px solid #e5e7eb', lineHeight: 1.5,
            }}>
              С вашими событиями ещё не контактировали люди с таким именем.
            </div>
          )}

          {/* Manual add button */}
          {showManualButton && (
            <button
              onClick={() => { setManualName(query.trim()); setShowManual(true); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'none', border: 'none',
                color: '#9ca3af', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                padding: '2px 0', marginBottom: 4,
              }}
            >
              <IconPlus />
              Ввести данные вручную
            </button>
          )}

          {/* Manual input */}
          {showManual && (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <div style={{
                  position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
                  color: '#9ca3af', pointerEvents: 'none',
                }}>
                  <IconUser />
                </div>
                <input
                  autoFocus
                  value={manualName}
                  onChange={e => setManualName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddManual()}
                  placeholder="Имя участника..."
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '9px 10px 9px 32px',
                    borderRadius: 9, border: '1.5px solid #e5e7eb',
                    fontSize: 14, color: '#111827', background: '#fff',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#6366f1'}
                  onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <button
                onClick={handleAddManual}
                disabled={!manualName.trim() || adding}
                title="Добавить"
                style={{
                  padding: '8px 10px', borderRadius: 9,
                  border: 'none',
                  background: manualName.trim() ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#e5e7eb',
                  color: manualName.trim() ? '#fff' : '#9ca3af',
                  cursor: manualName.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s', flexShrink: 0,
                  width: 36, height: 36,
                }}
              >
                <IconPlus />
              </button>
              <button
                onClick={() => { setShowManual(false); setManualName(''); }}
                title="Отмена"
                style={{
                  padding: '8px 10px', borderRadius: 9,
                  border: '1.5px solid #e5e7eb', background: '#f9fafb',
                  color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, width: 36, height: 36,
                }}
              >
                <IconClose />
              </button>
            </div>
          )}
        </>
      )}

      {mode === 'list' && (
        <>
          {/* Шаг 1: ввод */}
          {resolved === null && (
            <>
              <textarea
                value={listText}
                onChange={e => setListText(e.target.value)}
                placeholder={'Введите по одному на строку\nНапример: Иван Петров\nили @username — для Telegram-аккаунта'}
                rows={5}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px',
                  borderRadius: 9, border: '1.5px solid #e5e7eb',
                  fontSize: 14, color: '#111827', background: '#fff',
                  outline: 'none', resize: 'vertical', lineHeight: 1.5,
                  fontFamily: 'inherit',
                }}
                onFocus={e => e.target.style.borderColor = '#6366f1'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
              <button
                onClick={handleResolve}
                disabled={resolving || entriesCount === 0}
                style={{
                  marginTop: 8, width: '100%', padding: '10px 0',
                  borderRadius: 9, border: 'none',
                  background: entriesCount > 0 ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#e5e7eb',
                  color: entriesCount > 0 ? '#fff' : '#9ca3af',
                  fontSize: 14, fontWeight: 700,
                  cursor: entriesCount > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                {resolving ? 'Проверяю...' : `Проверить (${entriesCount})`}
              </button>
            </>
          )}

          {/* Шаг 2: превью */}
          {resolved !== null && (
            <>
              <div style={{
                background: '#fff', borderRadius: 9, border: '1.5px solid #e5e7eb',
                overflow: 'hidden', marginBottom: 8, maxHeight: 260, overflowY: 'auto',
              }}>
                {resolved.map((item, i) => {
                  const badge = RESOLVE_BADGE[item.status] || RESOLVE_BADGE.INVALID;
                  // Показываем оригинальный ввод пользователя (с @username если был)
                  const rawLine = rawDisplayLines[i] || item.displayName || item.rawInput || '—';
                  // Имя/алиас, введённые в строке (без @username).
                  const namePart = stripUsername(rawLine);
                  // «Сырой» текущий alias члена: для гостя — его имя, для реального — пометка/алиас.
                  const currentAlias = item.currentAlias || '';
                  // Можно ли опционально применить правку: член существует и введённое имя/алиас
                  // отличается от текущего значения в базе.
                  const canEdit = EDITABLE_EXISTING.has(item.status)
                    && item.organizationMemberId != null
                    && namePart
                    && namePart !== currentAlias;
                  // Информационная подпись «в базе …» для существующих строк без правки (напр. уже в событии).
                  const infoExisting = !canEdit && SHOWS_EXISTING.has(item.status) && item.existingAlias
                    && item.existingAlias !== rawLine.trim()
                    ? item.existingAlias
                    : null;
                  const edit = applyEdits[i] || { on: false, value: namePart };
                  // Для реального меняем «алиас» (а не «пометку»), для гостя — «имя».
                  const editLabel = item.guest ? 'Применить изменение имени' : 'Применить изменение алиаса';
                  const willChange = item.guest ? 'имя будет обновлено' : 'алиас будет обновлён';
                  const currentRef = item.guest ? (currentAlias || '—') : (currentAlias || 'не задан');
                  return (
                    <div
                      key={i}
                      style={{
                        borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
                        opacity: ADDABLE.has(item.status) ? 1 : 0.6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {renderLineWithUsername(rawLine)}
                          </div>
                          {infoExisting && (
                            <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              в базе: «{infoExisting}»
                            </div>
                          )}
                        </div>

                        {/* Правый столбец: бейдж сверху, под ним — тумблер правки с пояснением. */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0, maxWidth: '58%' }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
                            color: badge.color, background: badge.bg,
                            textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap',
                          }}>
                            {badge.label}
                          </span>

                          {/* Появляющийся тумблер: применить изменение имени/алиаса (по умолчанию выкл). */}
                          {canEdit && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, minWidth: 0 }}>
                                <span style={{ fontSize: 11, color: '#6b7280', textAlign: 'right' }}>{editLabel}</span>
                                <span style={{ fontSize: 10, color: edit.on ? '#16a34a' : '#9ca3af', textAlign: 'right' }}>
                                  в базе «{currentRef}» · {edit.on ? willChange : 'не изменится'}
                                </span>
                                {edit.on && (
                                  <input
                                    value={edit.value}
                                    onChange={e => setApplyEdits(prev => ({ ...prev, [i]: { on: true, value: e.target.value } }))}
                                    placeholder={item.guest ? 'Имя участника...' : 'Алиас...'}
                                    style={{
                                      width: 150, maxWidth: '100%', boxSizing: 'border-box',
                                      padding: '5px 8px', borderRadius: 7, border: '1.5px solid #c7d2fe',
                                      fontSize: 12, color: '#111827', outline: 'none', textAlign: 'right',
                                    }}
                                  />
                                )}
                              </div>
                              <MiniToggle
                                on={edit.on}
                                onChange={() => setApplyEdits(prev => ({
                                  ...prev,
                                  [i]: { on: !(prev[i]?.on), value: prev[i]?.value ?? namePart },
                                }))}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setResolved(null); setApplyEdits({}); }}
                  disabled={batchAdding}
                  style={{
                    flex: '0 0 auto', padding: '10px 14px', borderRadius: 9,
                    border: '1.5px solid #e5e7eb', background: '#f9fafb',
                    color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Назад
                </button>
                <button
                  onClick={handleBatchAdd}
                  disabled={batchAdding || addableCount === 0}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 9, border: 'none',
                    background: addableCount > 0 ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : '#e5e7eb',
                    color: addableCount > 0 ? '#fff' : '#9ca3af',
                    fontSize: 14, fontWeight: 700,
                    cursor: addableCount > 0 ? 'pointer' : 'not-allowed',
                  }}
                >
                  {batchAdding ? 'Добавляю...' : `Добавить ${addableCount}`}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AddParticipantPanel;
